import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AdminRole, InvoiceStatus, InvoiceSubStatus, trimDecimalZeros } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/status-badge";
import { Amount, FiatAmount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { HashLink } from "@/components/hash-link";
import {
  INVOICE_STATUS_COLOR,
  REFUND_STATUS_COLOR,
  TRANSACTION_STATUS_COLOR,
  UNATTACHED_DEPOSIT_STATUS_COLOR,
  WEBHOOK_DELIVERY_STATUS_COLOR,
  WEBHOOK_EVENT_COLOR,
} from "@/lib/status-colors";
import { hasRole } from "@/lib/roles";
import { formatStatusLabel } from "@/lib/utils";
import { ApiError } from "@/api/client";
import { getInvoice, retryInvoiceSettlement } from "@/api/invoices";
import { type InvoiceTransactionItem } from "@/api/types";

// Real implementation of Task 26 Part D: the invoice 360° detail page,
// replacing the old in-page `InvoiceDetailDialog` (`_app.invoices.tsx`,
// pre-Task-26). Mostly read-only — INVOICES_VIEWER never mutates an invoice
// from here, matching the old dialog's own doc comment and the spec's "360°
// view" wording — with one deliberate exception: an INVOICES_MANAGER-only
// "Retry settlement" button, shown only on ACTION_REQUIRED/SETTLEMENT_FAILED
// invoices, for manual re-initiation once ops has fixed whatever kept the
// sweep/gas-fund declining (feature-013's long-promised "future backoffice"
// resolution path).
//
// The Summary section leads with the shared Amount/FiatAmount/AssetLabel
// components — the same convention used everywhere else in the app
// (invoices list, merchant detail's Invoices tab, the high-risk queue's
// case-detail dialog). The Transactions/Refunds/Webhook-deliveries/Ledger-
// entries sections below still use this file's own local formatCrypto/
// formatTimestamp/truncateHash helpers — a wider migration is a separate
// pass, out of scope here.
export const Route = createFileRoute("/_app/invoices_/$invoiceId")({
  component: InvoiceDetailPage,
});

// ─── formatting helpers ─────────────────────────────────────────────────────

const USD_FORMATTER = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

function formatFiat(amount: string): string {
  return USD_FORMATTER.format(Number(amount));
}

function formatCrypto(amount: string | null, symbol: string): string {
  return amount === null ? "—" : `${trimDecimalZeros(amount)} ${symbol}`;
}

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

function truncateHash(hash: string): string {
  return hash.length <= 16 ? hash : `${hash.slice(0, 8)}…${hash.slice(-6)}`;
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </dd>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/**
 * RBF-chain note for a transaction whose `replacedTransactionId` is
 * non-null — looks the replaced transaction up within the same already-
 * fetched `transactions` array (no extra request). Renders nothing for a
 * transaction that replaces nothing.
 */
function ReplacesNote({
  tx,
  transactions,
}: {
  tx: InvoiceTransactionItem;
  transactions: InvoiceTransactionItem[];
}) {
  if (tx.replacedTransactionId === null) return null;
  const replaced = transactions.find((t) => t.id === tx.replacedTransactionId);
  const label = replaced ? `${formatStatusLabel(replaced.operation)} ${replaced.txHash ? truncateHash(replaced.txHash) : replaced.id}` : tx.replacedTransactionId;
  return <div className="mt-0.5 text-xs text-muted-foreground">replaces {label}</div>;
}

function InvoiceDetailPage() {
  const { invoiceId } = Route.useParams();
  const { admin } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const canRetrySettlement = hasRole(admin.roles, AdminRole.INVOICES_MANAGER);

  const {
    data: invoice,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["invoice", invoiceId],
    queryFn: () => getInvoice(invoiceId),
  });

  const retryMutation = useMutation({
    mutationFn: () => retryInvoiceSettlement(invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice", invoiceId] });
      toast.success("Settlement retried — invoice is back in the sweep queue.");
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "Failed to retry settlement.");
    },
  });

  if (isPending) {
    return <div className="py-10 text-center text-sm text-muted-foreground">Loading invoice…</div>;
  }
  if (isError || !invoice) {
    return <div className="py-10 text-center text-sm text-muted-foreground">Failed to load invoice.</div>;
  }

  const hasLedgerEntries = invoice.merchantLedgerTransaction || invoice.projectLedgerTransaction;
  const showRetryButton =
    canRetrySettlement &&
    invoice.status === InvoiceStatus.ACTION_REQUIRED &&
    invoice.subStatus === InvoiceSubStatus.SETTLEMENT_FAILED;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{invoice.publicId}</h2>
          <p className="text-sm text-muted-foreground">
            {invoice.merchant.businessName} · {invoice.merchant.email}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge
            label={invoice.subStatus ?? invoice.status}
            color={INVOICE_STATUS_COLOR[invoice.status]}
          />
          {showRetryButton && (
            <Button
              type="button"
              size="sm"
              disabled={retryMutation.isPending}
              onClick={() => retryMutation.mutate()}
            >
              {retryMutation.isPending ? "Retrying…" : "Retry settlement"}
            </Button>
          )}
        </div>
      </div>

      <SectionCard title="Summary">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-4 border-b pb-4">
          <div>
            <div className="text-2xl font-semibold tracking-tight">
              <FiatAmount amount={invoice.fiatAmount} currency={invoice.fiatCurrency} />
            </div>
            <div className="mt-0.5 text-sm text-muted-foreground">
              <Amount native={invoice.cryptoAmount} symbol={invoice.asset.symbol} usd={invoice.cryptoAmountUsd} inline />
            </div>
          </div>
          <AssetLabel symbol={invoice.asset.symbol} network={invoice.asset.network} inline />
        </div>
        <dl className="space-y-2 text-sm">
          <DetailRow
            label="Fee"
            value={<Amount native={invoice.cryptoFee} symbol={invoice.asset.symbol} usd={invoice.feeUsd} inline />}
          />
          <DetailRow label="Created" value={formatTimestamp(invoice.createdAt)} />
          <DetailRow label="Expires" value={formatTimestamp(invoice.expiresAt)} />
          <DetailRow label="Settling at" value={formatTimestamp(invoice.settlingAt)} />
        </dl>
      </SectionCard>

      {invoice.resolution && (
        <SectionCard title="Resolution">
          <dl className="space-y-2 text-sm">
            <DetailRow label="Resolved by" value={invoice.resolution.resolvedBy} />
            <DetailRow label="Resolved at" value={formatTimestamp(invoice.resolution.resolvedAt)} />
            <DetailRow label="Resolved from" value={invoice.resolution.resolvedFromSubStatus} />
            <DetailRow
              label="Original fiat amount"
              value={`${invoice.fiatCurrency} ${invoice.resolution.originalFiatAmount}`}
            />
            <DetailRow
              label="Original crypto amount"
              value={formatCrypto(invoice.resolution.originalCryptoAmount, invoice.asset.symbol)}
            />
            <DetailRow label="Original USD rate" value={formatFiat(invoice.resolution.originalUsdRate)} />
          </dl>
        </SectionCard>
      )}

      {invoice.aml && (
        <SectionCard title="AML resolution">
          <dl className="space-y-2 text-sm">
            <DetailRow label="Resolved by" value={invoice.aml.amlResolvedBy.displayName} />
            <DetailRow label="Resolved at" value={formatTimestamp(invoice.aml.amlResolvedAt)} />
            <DetailRow label="Action" value={invoice.aml.amlResolutionAction} />
            <DetailRow label="Note" value={invoice.aml.amlResolutionNote ?? "—"} />
          </dl>
        </SectionCard>
      )}

      <SectionCard title="Transactions">
        {invoice.transactions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No transactions.</p>
        ) : (
          <div className="overflow-hidden overflow-x-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted text-left font-semibold text-muted-foreground">
                  <th className="px-3 py-2">Operation</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-right">Network fee</th>
                  <th className="px-3 py-2">Tx hash</th>
                  <th className="px-3 py-2">Counterparty</th>
                  <th className="px-3 py-2">Last event</th>
                </tr>
              </thead>
              <tbody>
                {invoice.transactions.map((tx) => (
                  <tr key={tx.id} className="border-t">
                    <td className="px-3 py-2 font-medium">
                      {formatStatusLabel(tx.operation)}
                      <ReplacesNote tx={tx} transactions={invoice.transactions} />
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge label={formatStatusLabel(tx.status)} color={TRANSACTION_STATUS_COLOR[tx.status]} />
                    </td>
                    <td className="px-3 py-2 text-right" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatCrypto(tx.amount, tx.asset.symbol)}
                    </td>
                    <td className="px-3 py-2 text-right" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatCrypto(tx.networkFee, tx.feeCurrency ?? tx.asset.symbol)}
                    </td>
                    <td className="px-3 py-2">
                      <HashLink value={tx.txHash} type="tx" network={tx.asset.network} />
                    </td>
                    <td className="px-3 py-2">
                      <HashLink value={tx.counterpartyAddress} type="address" network={tx.asset.network} />
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{formatTimestamp(tx.lastEventAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Refunds">
        {invoice.refunds.length === 0 ? (
          <p className="text-sm text-muted-foreground">No refunds.</p>
        ) : (
          <div className="overflow-hidden overflow-x-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted text-left font-semibold text-muted-foreground">
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Initiator</th>
                  <th className="px-3 py-2">Claimant</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Address</th>
                  <th className="px-3 py-2">Claimed</th>
                </tr>
              </thead>
              <tbody>
                {invoice.refunds.map((refund) => (
                  <tr key={refund.id} className="border-t">
                    <td className="px-3 py-2">
                      <StatusBadge label={formatStatusLabel(refund.status)} color={REFUND_STATUS_COLOR[refund.status]} />
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{formatStatusLabel(refund.initiator)}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {refund.claimant ? formatStatusLabel(refund.claimant) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatCrypto(refund.amount, invoice.asset.symbol)}
                    </td>
                    <td className="px-3 py-2">
                      <HashLink value={refund.address} type="address" network={invoice.asset.network} />
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{formatTimestamp(refund.claimedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* feature-037: deposits received after the invoice closed — never part of it. */}
      {invoice.unattachedDeposits.length > 0 && (
        <SectionCard title="Unattached deposits">
          <ul className="space-y-1.5 text-sm">
            {invoice.unattachedDeposits.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-2">
                <Link
                  to="/unattached-deposits/$unattachedDepositId"
                  params={{ unattachedDepositId: d.id }}
                  className="font-mono hover:underline"
                >
                  {d.publicId}
                </Link>
                <span className="flex items-center gap-2">
                  {d.cryptoAmount} {invoice.asset.symbol}
                  <StatusBadge
                    label={formatStatusLabel(d.subStatus ?? d.status)}
                    color={UNATTACHED_DEPOSIT_STATUS_COLOR[d.status]}
                  />
                </span>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      <SectionCard title="Webhook deliveries">
        {invoice.webhookDeliveries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No webhook deliveries.</p>
        ) : (
          <div className="overflow-hidden overflow-x-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted text-left font-semibold text-muted-foreground">
                  <th className="px-3 py-2">Event</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Attempts</th>
                  <th className="px-3 py-2">Last response</th>
                  <th className="px-3 py-2">Delivered at</th>
                </tr>
              </thead>
              <tbody>
                {invoice.webhookDeliveries.map((delivery) => (
                  <tr key={delivery.id} className="border-t">
                    <td className="px-3 py-2 font-medium">
                      <StatusBadge label={delivery.eventType} color={WEBHOOK_EVENT_COLOR[delivery.eventType]} />
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge
                        label={delivery.status}
                        color={WEBHOOK_DELIVERY_STATUS_COLOR[delivery.status]}
                      />
                    </td>
                    <td className="px-3 py-2 text-right" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {delivery.attemptCount}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{delivery.lastResponseCode ?? "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {delivery.deliveredAt ? formatTimestamp(delivery.deliveredAt) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Ledger entries">
        {!hasLedgerEntries ? (
          <p className="text-sm text-muted-foreground">No ledger entries yet.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {invoice.merchantLedgerTransaction && (
              <div className="rounded-lg border p-4">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Merchant History
                </h4>
                <dl className="space-y-2 text-sm">
                  <DetailRow label="Operation" value={invoice.merchantLedgerTransaction.operation} />
                  <DetailRow
                    label="Amount"
                    value={formatCrypto(invoice.merchantLedgerTransaction.amountNative, invoice.asset.symbol)}
                  />
                  <DetailRow
                    label="Amount (USD)"
                    value={formatFiat(invoice.merchantLedgerTransaction.amountUsd)}
                  />
                </dl>
              </div>
            )}
            {invoice.projectLedgerTransaction && (
              <div className="rounded-lg border p-4">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Project History
                </h4>
                <dl className="space-y-2 text-sm">
                  <DetailRow label="Operation" value={invoice.projectLedgerTransaction.operation} />
                  <DetailRow
                    label="Amount"
                    value={formatCrypto(invoice.projectLedgerTransaction.amountNative, invoice.asset.symbol)}
                  />
                  <DetailRow
                    label="Amount (USD)"
                    value={formatFiat(invoice.projectLedgerTransaction.amountUsd)}
                  />
                  <DetailRow
                    label="Gas cost"
                    value={formatCrypto(invoice.projectLedgerTransaction.gasCostNative, invoice.asset.symbol)}
                  />
                  <DetailRow
                    label="Gas cost (USD)"
                    value={formatFiat(invoice.projectLedgerTransaction.gasCostUsd)}
                  />
                </dl>
              </div>
            )}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
